import { TicketOptionWithQuantity } from '@repo/types';
import { QuantityError, ValidationContext } from './types';
import {
    getEffectiveCapacity,
    hasActiveWaitingList,
    productInstanceHasActiveWaitingList,
} from './utils';

export const validateIndividualMinEntrants = (
    ticket: TicketOptionWithQuantity,
    context: ValidationContext,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    const travelers = ticket.quantity * ticket.occupancy;

    // Skip validation for zero quantities UNLESS explicitly required
    // In joined trips with requireAllValid, even zero quantities must be validated
    const requireExplicitlyAllowedZeroQuantity = context.hasJoinedTrip && context.requireAllValid;
    if (travelers === 0 && !requireExplicitlyAllowedZeroQuantity) {
        return errors;
    }

    if (ticket.minEntrants !== null && travelers < ticket.minEntrants) {
        errors.push({
            id: ticket.ticketCategoryId,
            error: 'minEntrants',
            value: ticket.minEntrants,
            type: 'individual',
        });
    }

    return errors;
};

export const validateIndividualCapacity = (
    ticket: TicketOptionWithQuantity,
    _context: ValidationContext,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    // Check if waiting list allows overbooking (bypass capacity validation)
    // Only bypass when capacity is full (isClosedForBooking: true)
    if (hasActiveWaitingList(ticket)) {
        return errors;
    }

    const effectiveCapacity = getEffectiveCapacity(ticket);

    if (ticket.quantity > effectiveCapacity) {
        errors.push({
            id: ticket.ticketCategoryId,
            error: 'capacity',
            value: effectiveCapacity,
            type: 'individual',
        });
    }

    return errors;
};

// CRITICAL: This validates the TOTAL across all tickets sharing the instance,
// NOT per-ticket validation. A product instance minEntrants=3 means
// Adult(2) + Child(1) = 3 total is valid.
export const validateSharedInstanceMinEntrants = (
    tickets: TicketOptionWithQuantity[],
    context: ValidationContext,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    const requireValidateZeroQuantities = context.hasJoinedTrip && context.requireAllValid;

    // In non-joined-trip collection mode (e.g. accommodation: a Unit-priced
    // cabin row + a per-person extras row sharing a project_collection_id),
    // siblings have cross-dependency semantics: per-availability min_entrants
    // on one sibling must fire once the user has engaged with the collection,
    // even if that sibling itself is at zero. Promote the zero-skip from
    // per-PI scope to per-collection scope for that shape. Joined-trip cases
    // are unchanged — they already validate zero quantities when
    // requireAllValid (Case 1) and legitimately allow optional categories
    // when !requireAllValid (Case 2).
    const collectionHasActivity =
        context.hasCollectionMode && !context.hasJoinedTrip && tickets.some((t) => t.quantity > 0);

    const instanceMap = new Map<
        string,
        {
            minEntrants: number;
            ticketIds: Set<string>;
        }
    >();

    for (const ticket of tickets) {
        for (const pi of ticket.productInstances) {
            if (pi.minEntrants === null) continue;

            if (ticket.minEntrants !== null) continue;

            const instanceId = pi.id;
            if (!instanceMap.has(instanceId)) {
                instanceMap.set(instanceId, {
                    minEntrants: pi.minEntrants,
                    ticketIds: new Set(),
                });
            }
            instanceMap.get(instanceId)!.ticketIds.add(ticket.ticketCategoryId);
        }
    }

    for (const [_instanceId, info] of instanceMap.entries()) {
        const ticketsForInstance = tickets.filter((t) => info.ticketIds.has(t.ticketCategoryId));

        const totalTravelers = ticketsForInstance.reduce(
            (sum, t) => sum + t.quantity * t.occupancy,
            0,
        );

        if (totalTravelers === 0 && !requireValidateZeroQuantities && !collectionHasActivity)
            continue;

        if (totalTravelers < info.minEntrants) {
            // Use 'individual' type when only one ticket is affected (not actually shared)
            // Use 'total' type when multiple tickets share the same instance
            const errorType = ticketsForInstance.length === 1 ? 'individual' : 'total';

            for (const ticket of ticketsForInstance) {
                errors.push({
                    id: ticket.ticketCategoryId,
                    error: 'minEntrants',
                    value: info.minEntrants,
                    type: errorType,
                });
            }
        }
    }

    return errors;
};

export const validateCollectionMinEntrants = (
    tickets: TicketOptionWithQuantity[],
    context: ValidationContext,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    if (context.collectionMinEntrants === null) {
        return errors;
    }

    const totalTravelers = tickets.reduce(
        (sum, ticket) => sum + ticket.quantity * ticket.occupancy,
        0,
    );

    if (totalTravelers < context.collectionMinEntrants) {
        errors.push({
            id: 'collection',
            error: 'minEntrants',
            value: context.collectionMinEntrants,
            type: 'total',
        });
    }

    return errors;
};

// CRITICAL BUG FIX: Old code incorrectly validated individual quantity instead of total
// This implementation correctly validates the sum of all travelers.
export const validateCollectionCapacity = (
    tickets: TicketOptionWithQuantity[],
    context: ValidationContext,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    if (context.maxEntrantsPerBooking === null) {
        return errors;
    }

    const totalTravelers = tickets.reduce(
        (sum, ticket) => sum + ticket.quantity * ticket.occupancy,
        0,
    );

    // CRITICAL: Validate against TOTAL, not individual quantity
    if (totalTravelers > context.maxEntrantsPerBooking) {
        for (const ticket of tickets) {
            errors.push({
                id: ticket.ticketCategoryId,
                error: 'capacity',
                value: context.maxEntrantsPerBooking,
                type: 'total',
            });
        }
    }

    return errors;
};

/**
 * SB-933: validate the merged product instance's `capacity` as a TOTAL across
 * sibling ticket options of the same collection. The existing
 * `validateSharedProductInstanceCapacity` rule only fires when tickets share a
 * `pi.id`, which they don't after collection-mode merging (each ticket option
 * keeps its pre-merge PI reference for reservation expansion downstream). This
 * rule sources the shared cap from the merged PI's `capacity` via context.
 */
export const validateCollectionResourceCapacity = (
    tickets: TicketOptionWithQuantity[],
    context: ValidationContext,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    if (context.sharedResourceCapacity === null) {
        return errors;
    }

    const totalTravelers = tickets.reduce(
        (sum, ticket) => sum + ticket.quantity * ticket.occupancy,
        0,
    );

    if (totalTravelers > context.sharedResourceCapacity) {
        for (const ticket of tickets) {
            errors.push({
                id: ticket.ticketCategoryId,
                error: 'capacity',
                value: context.sharedResourceCapacity,
                type: 'total',
            });
        }
    }

    return errors;
};

export const validateJoinedTripCapacity = (
    tickets: TicketOptionWithQuantity[],
    context: ValidationContext,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    if (context.globalMaxEntrants === null) {
        return errors;
    }

    const totalTravelers = tickets.reduce(
        (sum, ticket) => sum + ticket.quantity * ticket.occupancy,
        0,
    );

    const availableCapacity = context.globalMaxEntrants - context.currentConsumedCapacity;

    if (totalTravelers > availableCapacity) {
        for (const ticket of tickets) {
            errors.push({
                id: ticket.ticketCategoryId,
                error: 'capacity',
                value: availableCapacity,
                type: 'total',
            });
        }
    }

    return errors;
};

export const validateNotEmpty = (
    tickets: TicketOptionWithQuantity[],
    attemptedBooking: boolean,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    if (!attemptedBooking) {
        return errors;
    }

    const hasAnyQuantity = tickets.some((ticket) => ticket.quantity > 0);

    if (!hasAnyQuantity) {
        errors.push({
            id: 'totalMinEntrants',
            error: 'minEntrants',
            value: 1,
            type: 'total',
        });
    }

    return errors;
};

// When multiple tickets share the same product instance, the total quantity
// across all tickets must not exceed the instance capacity.
export const validateSharedProductInstanceCapacity = (
    tickets: TicketOptionWithQuantity[],
    _context: ValidationContext,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    const instanceMap = new Map<string, Set<string>>();

    for (const ticket of tickets) {
        for (const pi of ticket.productInstances) {
            const instanceId = pi.id;
            if (!instanceMap.has(instanceId)) {
                instanceMap.set(instanceId, new Set());
            }
            instanceMap.get(instanceId)!.add(ticket.ticketCategoryId);
        }
    }

    for (const [instanceId, ticketIdSet] of instanceMap.entries()) {
        if (ticketIdSet.size <= 1) {
            continue;
        }

        const ticketsForInstance = tickets.filter((t) => ticketIdSet.has(t.ticketCategoryId));

        const [firstTicket] = ticketsForInstance;
        const productInstance = firstTicket.productInstances.find((pi) => pi.id === instanceId);

        if (!productInstance || productInstance.capacity === null) {
            continue;
        }

        // Skip validation if waiting list is active (enabled AND closed for booking)
        // Waiting list bypass only applies when capacity is full (isClosedForBooking: true)
        if (productInstanceHasActiveWaitingList(productInstance)) {
            continue;
        }

        const totalQuantity = ticketsForInstance.reduce((sum, ticket) => sum + ticket.quantity, 0);

        if (totalQuantity > productInstance.capacity) {
            for (const ticket of ticketsForInstance) {
                errors.push({
                    id: ticket.ticketCategoryId,
                    error: 'capacity',
                    value: productInstance.capacity,
                    type: 'total',
                });
            }
        }
    }

    return errors;
};

export const validateJoinedTripMinEntrants = (
    tickets: TicketOptionWithQuantity[],
    context: ValidationContext,
): QuantityError[] => {
    const errors: QuantityError[] = [];

    if (context.collectionMinEntrants === null) {
        return errors;
    }

    const totalTravelers = tickets.reduce(
        (sum, ticket) => sum + ticket.quantity * ticket.occupancy,
        0,
    );

    // Skip validation if all quantities are zero UNLESS requireAllValid is true
    // When requireAllValid is false, users can optionally select tickets
    const hasAnyQuantity = tickets.some((ticket) => ticket.quantity > 0);
    if (!hasAnyQuantity && !context.requireAllValid) {
        return errors;
    }

    if (totalTravelers < context.collectionMinEntrants) {
        errors.push({
            id: 'collection',
            error: 'minEntrants',
            value: context.collectionMinEntrants,
            type: 'total',
        });
    }

    return errors;
};
